import { useMemo, useState } from 'react';
import SharedToolbar from '../../shared/shared-toolbar-latest';
import { createGridOverlays } from '../grid-overlay/no-data-overlay-helpers';
import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAllFeatures,
  updateAdminLabsFeature,
  UserKeys,
} from '../../api/users-api';
import { FeatureEntity } from '../../../types';
import LabsFeatureDialog from './admin-labs-feature-dialog';
import LabsDeleteFeatureDialog from './admin-labs-delete-dialog';
import { useUserSettings } from '../../utils/use-user-settings';
import SharedDataGridClient from '../../shared/shared-data-grid-client';
import CreateButton from '../../shared/buttons/create-button';
import SharedIconButton from '../../shared/buttons/shared-icon-button';
import Switch from '@mui/material/Switch';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

export default function AdminLabsPage() {
  const queryClient = useQueryClient();
  const { loggedInUser } = useUserSettings();
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data, isLoading, isFetching } = useQuery<FeatureEntity[]>({
    queryKey: [UserKeys.AdminLabsPage],
    queryFn: () => getAllFeatures(),
    meta: {
      errorMessage:
        'There was an issue loading data. Please refresh and try again.',
    },
  });
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowId[]>([]);
  const selectedRows = useMemo(
    () =>
      data?.filter((row) => selectedRowIds.includes(row.featureString)) ?? [],
    [data, selectedRowIds]
  );

  const updateHiddenMutation = useMutation({
    mutationFn: async (feature: FeatureEntity) => {
      updateAdminLabsFeature(feature);
    },
    onSuccess: (_data, updatedFeature) => {
      queryClient.setQueryData<FeatureEntity[]>(
        [UserKeys.AdminLabsPage],
        (oldData) =>
          oldData?.map((feature) =>
            feature.featureString === updatedFeature.featureString
              ? {
                  ...updatedFeature,
                  updatedBy: loggedInUser,
                  updatedDate: new Date().getTime(),
                }
              : feature
          ) ?? []
      );
    },
  });

  const columns: GridColDef<FeatureEntity>[] = [
    {
      field: 'featureString',
      headerName: 'Feature',
      flex: 1,
    },
    {
      field: 'featureName',
      headerName: 'Feature Label',
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      valueFormatter: (value: string) => value || 'N/A',
    },
    {
      field: 'creator',
      headerName: 'Creator',
      flex: 1,
    },
    {
      field: 'createdDate',
      headerName: 'Created Date',
      flex: 1,
      valueFormatter: (value: number) =>
        value ? new Date(value).toLocaleString() : 'N/A',
    },
    {
      field: 'updatedBy',
      headerName: 'Updated By',
      flex: 1,
      valueFormatter: (value: string) => value || 'N/A',
    },
    {
      field: 'updatedDate',
      headerName: 'Updated Date',
      flex: 1,
      valueFormatter: (value: number) =>
        value !== 0 ? new Date(value as number).toLocaleString() : 'N/A',
    },
    {
      field: 'hidden',
      headerName: 'Hidden',
      flex: 0.5,
      type: 'boolean',
      renderCell: (params) => (
        <Switch
          checked={!!params.value}
          onChange={(event) => {
            updateHiddenMutation.mutate({
              ...params.row,
              hidden: event.target.checked,
            });
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
  ];

  function CustomToolbar() {
    return (
      <SharedToolbar
        primaryButtons={
          <CreateButton
            onClick={() => setCreateModalOpen(true)}
            label="Add New"
          />
        }
        secondaryButtons={
          <>
            <SharedIconButton
              variant="edit"
              onClick={() => setUpdateModalOpen(true)}
              disabled={selectedRows.length !== 1}
              tooltipLabel="Edit"
            />
            <SharedIconButton
              variant="delete"
              onClick={() => {
                setDeleteModalOpen(true);
              }}
              disabled={selectedRows.length === 0}
              tooltipLabel="Delete"
            />
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: [UserKeys.AdminLabsPage],
                });
              }}
              tooltipLabel="Refresh"
            />
          </>
        }
      ></SharedToolbar>
    );
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Admin Labs', <InfoOutlined />),
        }}
        rows={data || []}
        getRowId={(row) => row.featureString}
        columns={columns}
        loading={isLoading || isFetching}
        checkboxSelection
        selectedRows={selectedRowIds}
        setSelectedRows={setSelectedRowIds}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
      {isCreateModalOpen && (
        <LabsFeatureDialog
          open={isCreateModalOpen}
          handleClose={() => setCreateModalOpen(false)}
        />
      )}
      {isUpdateModalOpen && (
        <LabsFeatureDialog
          open={isUpdateModalOpen}
          handleClose={() => setUpdateModalOpen(false)}
          featureToEdit={selectedRows[0]}
        />
      )}
      {isDeleteModalOpen && (
        <LabsDeleteFeatureDialog
          open={isDeleteModalOpen}
          handleClose={() => setDeleteModalOpen(false)}
          featureNames={selectedRows.map((row) => row.featureString)}
        />
      )}
    </>
  );
}
